import { createApi } from '@reduxjs/toolkit/query/react';
import { PagedResult } from '../../types/api';
import { config } from '../../constants/config';
import { createBaseQuery } from './baseQuery';

export interface Organization {
  id: string;
  organizationId: string;
  name: string;
  businessSystemId?: string;
  businessSystemName?: string;
  useSystemCertificate: boolean;
  created: string;
  modified: string;
}

export interface OrganizationStats {
  id: string;
  organizationId: string;
  name: string;
  businessSystemId?: string;
  businessSystemName?: string;
  useSystemCertificate: boolean;
  created: string;
  modified: string;
  totalApiClients: number;
  activeApiClients: number;
  lockedApiClients: number;
  failedApiClients: number;
  createdApiClients: number;
  totalManagementClients: number;
  lastActivity?: string;
  certificatesCount: number;
  isDeleted?: boolean;
}

export interface OrganizationFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  businessSystemId?: string;
  sortBy?: string;
  sortDescending?: boolean;
  useSystemCertificate?: boolean;
}

export interface CreateOrganizationRequest {
  organizationId: string;
  name: string;
  businessSystemId?: string;
}

export interface UpdateOrganizationRequest {
  id: string;
  organizationId: string;
  name: string;
  businessSystemId?: string;
  useSystemCertificate?: boolean;
}

export const organizationsApi = createApi({
  reducerPath: 'organizationsApi',
  baseQuery: createBaseQuery(`${config.apiUrl}/api`),
  tagTypes: ['Organization'],
  endpoints: (builder) => ({
    getOrganizations: builder.query<PagedResult<OrganizationStats>, OrganizationFilters>({
      query: (filters) => ({
        url: '/organizations',
        params: filters,
      }),
      providesTags: ['Organization'],
    }),

    getOrganizationsSimple: builder.query<Organization[], void>({
      query: () => '/organizations/simple',
      providesTags: ['Organization'],
    }),
    
    getOrganizationById: builder.query<OrganizationStats, string>({
      query: (id) => `/organizations/${id}`,
      providesTags: (result, error, id) => [{ type: 'Organization', id }],
    }),
    
    getOrganizationByOrganizationId: builder.query<Organization, string>({
      query: (organizationId) => `/organizations/by-organization-id/${organizationId}`,
      providesTags: (result, error, organizationId) => [{ type: 'Organization', id: organizationId }],
    }),
    
    createOrganization: builder.mutation<Organization, CreateOrganizationRequest>({
      query: (organization) => ({
        url: '/organizations',
        method: 'POST',
        body: organization,
      }),
      invalidatesTags: ['Organization'],
    }),
    
    updateOrganization: builder.mutation<Organization, UpdateOrganizationRequest>({
      query: ({ id, ...organization }) => ({
        url: `/organizations/${id}`,
        method: 'PUT',
        body: { id, ...organization },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Organization', id },
        'Organization'
      ],
    }),
    
    deleteOrganization: builder.mutation<void, string>({
      query: (id) => ({
        url: `/organizations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Organization', id },
        'Organization'
      ],
    }),

    setOrganizationSystemCertificate: builder.mutation<void, { id: string; useSystemCertificate: boolean }>({
      query: ({ id, useSystemCertificate }) => ({
        url: `/organizations/${id}/setSystemCertificate`,
        method: 'POST',
        body: { UseSystemCertificate: useSystemCertificate },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Organization', id },
        'Organization'
      ],
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationsSimpleQuery,
  useGetOrganizationByIdQuery,
  useGetOrganizationByOrganizationIdQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useSetOrganizationSystemCertificateMutation,
} = organizationsApi;